/**
 * @flow
 * @format
 */

import React from 'react';
import { connect } from 'react-redux';
import { pure } from 'recompose';
import _ from 'lodash';
import { pick } from 'lodash/fp';
import { Card, Row, Col } from 'reactstrap';
import cc from 'classcat';
import ErrorBoundary from 'react-error-boundary';

import FaQuestionCircle from 'react-icons/lib/fa/question-circle';

import { createHTMLMarkup, branch } from '~/utils';
import * as designSelectors from '~/selectors/design';

import type { Connector } from 'react-redux';

import HoverTooltip from '../HoverTooltip';

import FieldTypeInput from '../FieldTypeInput';
import FieldRadioGroup from '../FieldRadioGroup';
import FieldStacked from '../FieldStacked';
import TableSelectionGroup from '../TableSelectionGroup';
import PowerLossSummary from '../TableGroup/PowerSemiLossSummary';
import TableGroup from '../TableGroup';
import LargeTable from '../LargeTable';
import StackedGroup from '../StackedGroup';
import FieldDropdown from '../FieldDropdown';
import GraphGroup from '../GraphGroup';

import classes from './FieldWrapper.scss';

type OwnPropsType = {
  id: string,
  data: StepFieldData,
  onChangeHandler: (id: string) => any,
  onRevertHandler: (id: string) => any,
};

type PropsType = {
  isChanged: boolean,
  isEvaluating: boolean,
  currentValue: number | string | null,
  lastValue: number | string | null,
  defaultValue: number | string | null,
  dirty: any,
  error: string[],
  options: any,
  rangeMax: number,
  rangeMin: number,
  shouldRender: boolean,
  style?: {},
  warning: string[],
} & OwnPropsType;

export class FieldWrapper extends React.Component<PropsType & OwnPropsType, *> {
  static defaultProps = {
    error: [],
    warning: [],
    data: {
      type: '',
      step: '',
      calculated: false,
    },
    onChangeHandler: () => {},
    onRevertHandler: () => {},
    style: {},
  };

  onChange: Function;
  onRevert: Function;
  props: PropsType & OwnPropsType;

  shouldComponentUpdate(nextProps: PropsType) {
    const subProps = pick([
      'currentValue',
      'defaultValue',
      'dirty',
      'error',
      'isChanged',
      'isEvaluating',
      'rangeMax',
      'rangeMin',
      'shouldRender',
      'warning',
      'options',
    ]);

    return !_.isEqual(subProps(this.props), subProps(nextProps));
  }

  // eslint-disable-next-line react/sort-comp
  constructor(props: PropsType) {
    super(props);
    this.onChange = props.onChangeHandler(props.id);
    this.onRevert = () => {
      props.onRevertHandler(props.id);
    };
  }

  render() {
    const {
      currentValue,
      defaultValue,
      isChanged,
      isEvaluating,
      lastValue,
      rangeMin,
      rangeMax,
      dirty,
      error,
      warning,
    } = this.props;
    const { data } = this.props;

    // Break this render early if the deps aren't met.
    if (!this.props.shouldRender) {
      return null;
    }

    const type = data.type ? data.type : 'number';
    const step = data.step ? data.step : 'any';

    // const isLoading = (isEvaluating && errors.length === 0);
    const isLoading = false;
    const isDirty = dirty !== undefined;
    const isCalculated = data.calculated;
    const isModified = isChanged && !isDirty && !isCalculated;
    const isTable = type === 'table';
    const isSelectTable = type === 'selecttable';
    // const differs = defaultValue !== currentValue;
    const hasError = error.length > 0;
    const hasWarning = warning.length > 0;

    const markupName = createHTMLMarkup(data.name);
    const fieldName = data.name ? <h5>{markupName}</h5> : '';

    // debug(changed, dirty, isModified, differs);
    let tooltip = null;

    if (Array.isArray(data.tooltip)) {
      tooltip = data.tooltip.map((t, i: number) => {
        if (!t) return null;
        const title = _.get(this.props, ['currentValue', `${i + 1}`, '0'], '');
        return (
          <HoverTooltip
            key={`${this.props.id}-${_.camelCase(title)}`}
            id={`${this.props.id}-${_.camelCase(title)}`}
            title={createHTMLMarkup(title)}
            // text={createHTMLMarkup(data.tooltip[i])}
            text={createHTMLMarkup(t)}
          >
            <FaQuestionCircle className={classes.infoIcon} size={24} />
          </HoverTooltip>
        );
      });
    } else {
      tooltip = branch(
        _.isString(data.tooltip),
        <HoverTooltip
          id={this.props.id}
          title={markupName}
          text={createHTMLMarkup(data.tooltip)}
        >
          <FaQuestionCircle className={classes.infoIcon} size={24} />
        </HoverTooltip>,
        null,
      );
    }

    const width =
      this.props.id === 'SemiconLossSummaryTable'
        ? 6
        : process.env.REACT_APP_LEFT_PANE_SIZE;

    const leftCol = (
      <Col sm={width} className="d-flex">
        {fieldName}
        {branch(!['number', 'text', 'stack'].includes(type), tooltip)}
      </Col>
    );

    const isATable = isSelectTable || isTable;

    let cardClasses = {
      [classes.error]: hasError,
      [classes.warning]: hasWarning,
      [classes.changed]: isDirty || isLoading,
    };

    if (!isATable) {
      cardClasses = Object.assign({}, cardClasses, {
        [classes.calculated]: isCalculated,
        [classes.success]: isModified && !isLoading,
      });
    }

    // hack
    if (type === 'largetable') {
      cardClasses = {
        ...cardClasses,
        [classes.error]: hasError || hasWarning,
        [classes.warning]: null,
      };
    }

    const isSummaryTable = this.props.id === 'SemiconLossSummaryTable';
    const hasLeftCol =
      !isSummaryTable &&
      (!isATable &&
        ![
          'dropdown',
          'graph',
          'fieldstack',
          'stack',
          'radio',
          'largetable',
        ].includes(type));
    const padClass =
      isSelectTable || type === 'largetable' || isSummaryTable ? 'p-0' : '';

    const borderHackFix = type === 'graph' || isSummaryTable ? 'border-0' : '';

    // if (hasError) console.log(dirty, currentValue, this.props.lastValue);

    // Sometimes an error field doesn't show a new value (shows 0) so
    // we have to keep track of the last value we recieved.
    const displayValue = hasError ? lastValue : currentValue;

    const rightCol = () => {
      if (type === 'number' || type === 'text') {
        return (
          <FieldTypeInput
            id={this.props.id}
            value={displayValue}
            defaultVal={defaultValue}
            changed={isChanged}
            evaluating={isEvaluating}
            error={error}
            warning={warning}
            range={[rangeMin, rangeMax]}
            calculated={isCalculated || false}
            unit={data.unit}
            step={step}
            revertInput={this.onRevert}
            changeInput={this.onChange}
            type={type}
            tooltip={tooltip}
          />
        );
      }
      if (type === 'stack') {
        return (
          <StackedGroup
            calculated={isCalculated || false}
            value={displayValue}
            tooltip={tooltip}
            fieldName={fieldName}
          />
        );
      }
      if (type === 'graph') {
        return <GraphGroup id={this.props.id} chartName={data.name} />;
      }

      if (type === 'radio') {
        return (
          <FieldRadioGroup
            value={displayValue}
            defaultVal={defaultValue}
            changed={isChanged}
            evaluating={isEvaluating}
            error={error}
            warning={warning}
            fieldName={fieldName}
            revertInput={this.onRevert}
            changeInput={this.onChange}
            options={this.props.data.options}
            tooltip={tooltip}
          />
        );
      }
      if (type === 'dropdown') {
        return (
          <FieldDropdown
            value={displayValue}
            defaultVal={defaultValue}
            changed={isChanged}
            evaluating={isEvaluating}
            error={error}
            warning={warning}
            revertInput={this.onRevert}
            changeInput={this.onChange}
            options={this.props.options}
            fieldName={fieldName}
            tooltip={tooltip}
            multiple={this.props.data.multiple}
          />
        );
      }

      if (isSelectTable) {
        return (
          <TableSelectionGroup
            id={this.props.id}
            name={fieldName}
            tooltip={tooltip}
            value={displayValue}
            defaultVal={defaultValue}
            changed={isChanged}
            evaluating={isEvaluating}
            error={error}
            warning={warning}
            dataTarget={data.data}
            revertInput={this.onRevert}
            changeInput={this.onChange}
            options={data.options}
          />
        );
      }

      if (this.props.id === 'SemiconLossSummaryTable') {
        return (
          <PowerLossSummary
            id={this.props.id}
            value={displayValue}
            fieldName={fieldName}
            tooltip={tooltip}
            defaultVal={defaultValue}
            changed={isChanged}
            evaluating={isEvaluating}
            error={error}
            warning={warning}
            dataTarget={data.data}
            revertInput={this.onRevert}
            changeInput={this.onChange}
            options={data.options}
          />
        );
      }

      if (type === 'fieldstack') {
        return (
          <FieldStacked
            id={this.props.id} // $FlowFixMe
            value={_.tail((displayValue: any))}
            unit={data.unit}
            fieldName={fieldName}
            type={type}
            tooltip={tooltip}
            readOnly={isCalculated || false}
          />
        );
      }
      if (isTable) {
        return (
          <TableGroup
            id={this.props.id}
            value={displayValue}
            defaultVal={defaultValue}
            changed={isChanged}
            evaluating={isEvaluating}
            error={error}
            warning={warning}
            dataTarget={data.data}
            revertInput={this.onRevert}
            changeInput={this.onChange}
            options={data.options}
          />
        );
      }
      if (type === 'largetable') {
        return (
          <LargeTable
            id={this.props.id}
            value={displayValue}
            defaultVal={defaultValue}
            changed={isChanged}
            evaluating={isEvaluating}
            error={error}
            warning={warning}
            revertInput={this.onRevert}
            changeInput={this.onChange}
            options={this.props.options}
            fieldName={fieldName}
            tooltip={tooltip}
          />
        );
      }
      return null;
    };

    return (
      <ErrorBoundary>
        <Card
          id={this.props.id}
          className={cc([classes.card, cardClasses, padClass, borderHackFix])}
          style={this.props.style}
        >
          <Row>
            {branch(hasLeftCol, leftCol)}
            {rightCol()}
          </Row>
        </Card>
      </ErrorBoundary>
    );
  }
}

function mapStateToProps(
  state: AppStateType,
  ownProps: OwnPropsType,
): PropsType {
  const values = designSelectors.getFieldValues(state);
  const changedValues = designSelectors.getChangedFields(state);
  const dirtyValues = designSelectors.getDirtyFields(state);
  const fieldErrors = designSelectors.getFieldErrors(state);
  const fieldEvaulating = designSelectors.getEvaluatingFields(state);
  const fieldWarnings = designSelectors.getFieldWarnings(state);

  const data = values[ownProps.id];
  const dirty = dirtyValues[ownProps.id];
  const isEvaluating = Boolean(fieldEvaulating[ownProps.id]);
  const isChanged = Boolean(changedValues[ownProps.id]);

  let error: string[] = fieldErrors[ownProps.id];
  let warning: string[] = fieldWarnings[ownProps.id];

  // catch any radio buttons depending on other lists
  // TODO: hacky - should prob fix this at some point? maybe in reducer?
  let currentValue: string = _.get(data, ['currentValue']);
  let defaultValue = _.get(data, ['defaultValue']);
  let { options } = ownProps.data;

  const target = ownProps.data.dataTarget;
  if (target) {
    options = currentValue;
    // eslint-disable-next-line prefer-destructuring
    currentValue = values[target].currentValue;
    // eslint-disable-next-line prefer-destructuring
    defaultValue = values[target].defaultValue;

    // be sure and catch any dataTarget messages and show them!
    if (fieldWarnings[target]) {
      warning = fieldWarnings[target];
    }

    if (fieldErrors[target]) {
      error = fieldErrors[target];
    }

    /*
     * ONS#1034241
     * Edge case for Losscom_FET table - needs error instead of warning here.
     */
    if (target === 'PN_sel_fet') {
      error = fieldWarnings[ownProps.id] || fieldErrors[ownProps.id];
      warning = [];
    }
  }

  if (ownProps.data.type === 'stack') {
    // Handle adding quantity to the FET count
    const quantity = values.Num_pll_fet.currentValue;

    const fields: Array<*> = _.get(ownProps, ['data', 'fields'], []);
    // $FlowIgnore
    currentValue = fields.map(({ id, unit, name }: any) => {
      let val = values[id].currentValue;

      if (id === 'SelectedMOSFET') {
        val = `${val}\tQty = ${quantity}`;
      }
      return [id, val, unit, name];
    });
  }

  // check any field dependancies
  let shouldRender = true;
  const deps = _.get(ownProps, ['data', 'dependancies']);
  if (deps) {
    shouldRender = _.every(deps, (value: string | number, key: string) => {
      const fieldValue = _.get(values, [key, 'currentValue']);
      return fieldValue === value;
    });
  }

  return {
    ...ownProps,
    rangeMin: _.get(data, ['rangeValue', 'min']),
    rangeMax: _.get(data, ['rangeValue', 'max']),
    options,
    dirty,
    isEvaluating,
    isChanged,
    error,
    warning,
    shouldRender,
    currentValue,
    defaultValue,
    lastValue: _.get(state.design.lastValues[ownProps.id], ['currentValue']),
  };
}

const connector: Connector<OwnPropsType, PropsType> = connect(mapStateToProps);

export default connector(pure(FieldWrapper));



// WEBPACK FOOTER //
// ./src/components/FieldWrapper/FieldWrapper.js